import { observer } from 'mobx-react-lite'
import { Typography } from '@mui/material'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { ColorTokens, Palette } from 'src/feature/theme/model/Palette'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { PerformanceReviewInsightsDto } from '../../model/my-team-performance'

type PerformanceInsightsCardProps = {
  insights: PerformanceReviewInsightsDto | null
}

export const PerformanceInsightsCard = observer(
  ({ insights }: PerformanceInsightsCardProps) => {
    const { translation } = useLocalizationStore()

    const performanceReviewTranslation = translation.pages.myTeam.performanceReview

    if (!insights) {
      return null
    }

    const daysRemaining = insights.dueDate
      ? Math.max(
        0,
        Math.ceil(
          (new Date(insights.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
        ),
      )
      : null

    const isDeadlinePassed = daysRemaining !== null && daysRemaining < 1

    const completionText = performanceReviewTranslation.meetingDetail.completionProgress()
      .replace('%PERCENTAGE%', String(insights.completionRate))

    const participantsText = performanceReviewTranslation.meetingDetail.participantsCompleted()
      .replace('%COMPLETED%', String(insights.completedUsers))
      .replace('%TOTAL%', String(insights.totalUsers))

    const deadlineText = isDeadlinePassed
      ? performanceReviewTranslation.meetingDetail.deadlinePassed()
      : daysRemaining !== null
        ? performanceReviewTranslation.meetingDetail.daysRemaining()
          .replace('%DAYS%', String(daysRemaining))
        : ''

    return (
      <AppPaper sx={styles.root}>
        <Typography variant='h2' sx={styles.title}>
          {performanceReviewTranslation.insights()}
        </Typography>

        <Typography variant='body1' sx={styles.subtitle}>
          {completionText}.{' '}
          {participantsText}.{' '}
          {deadlineText}
        </Typography>
      </AppPaper>
    )
  },
)

const styles = {
  root: {
    borderRadius: '12px',
    padding: '10px 15px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    backgroundColor: Palette.CORAL_REEF_GREEN,
    justifyContent: 'center',
    width: '100%',
    border: 0,
  },
  title: {
    color: ColorTokens.neutral100,
  },
  subtitle: {
    marginTop: '-15px',
    color: ColorTokens.primary200,
  },
} as const